import React, { useEffect, useState } from 'react';
import {
  ArrowRightIcon,
  Box,
  Button,
  ExternalLinkIcon,
  InfoIcon,
  Link,
  RefreshIcon,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { StatusBadge } from 'components/SalaryComponents/components/StatusBadge';
import { TextWithDescription } from 'components/SalaryComponents/components/TextWithDescription';
import { helpArticles, salaryComponentParams } from 'components/SalaryComponents/constants';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import { getTableItemCountLimits } from 'components/SalaryComponents/utils';
import { formatBenefitAmount } from './utils';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { useFilters } from 'hooks/useFilters';
import { useHistory } from 'react-router-dom';
import { FlexibleBenefitsComponent, ListViewTableHeaderData } from './types';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { ListViewLoader } from 'components/ui/ListViewLoader';
import useFeature from 'hooks/useFeature';

export const FlexibleBenefitsListView = ({
  data,
  isLoading,
  isRefreshing,
  isError,
  headers,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  onRefresh,
}: {
  data: FlexibleBenefitsComponent[];
  isLoading: boolean;
  isRefreshing: boolean;
  isError: boolean;
  headers: ListViewTableHeaderData[];
  currentPage: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (limit: number) => void;
  onRefresh: () => void;
}) => {
  const history = useHistory();
  const { isFbpPercentagePrecisionEnabled } = useFeature('fbpPercentagePrecision');
  const [searchFilter, setSearchFilter] = useState('');

  const { openDetailsView } = useDetailsView(salaryComponentParams.componentId);

  const isTableEmpty = data.length === 0;
  const errorString = isError ? 'Failed to load data' : '';
  const { currentFilters, isAnyFilterApplied, applyFilter } = useFilters({
    filtersMap: {
      status: 'status',
      page: 'page',
      search: 'search',
    },
    onParamsChange(params) {},
  });
  const { debouncedValue } = useDebouncedValue(searchFilter, 500);

  useEffect(() => {
    applyFilter('search', debouncedValue);
  }, [debouncedValue]);

  return (
    <>
      <Table
        data={{ nodes: data }}
        isRefreshing={isRefreshing}
        pagination={
          !isTableEmpty ? (
            <TablePagination
              currentPage={currentPage}
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
              }}
              totalItemCount={data.length}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar
            title={getTableItemCountLimits(data.length, currentPage, itemsPerPage, 'FBP')}>
            <TableToolbarActions display={'flex'} flexWrap={'wrap'}>
              <Box display={'flex'} flexDirection={'row'} gap="spacing.4" flex={1} width="100%">
                <Box flex={1} width={'352px'}>
                  <SearchInput
                    label=""
                    placeholder="Search Component"
                    value={searchFilter}
                    onChange={({ value }) => setSearchFilter(value || '')}
                    onClearButtonClick={() => setSearchFilter('')}
                  />
                </Box>
                <Box flexShrink={0}>
                  <Button
                    size="medium"
                    onClick={() => {
                      history.push(routePaths.flexibleBenefits.create);
                    }}>
                    Create New
                  </Button>
                </Box>
                <Box flexShrink={0}>
                  <Button icon={RefreshIcon} variant={'tertiary'} onClick={onRefresh} />
                </Box>
              </Box>
            </TableToolbarActions>
          </TableToolbar>
        }
        showBorderedCells={false}>
        {(salaryComponents) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                {headers.map((header) => {
                  return (
                    <TableHeaderCell key={header.name}>
                      {header.tooltipText ? (
                        <Box
                          display={'flex'}
                          flexDirection={'row'}
                          gap={'spacing.3'}
                          alignItems={'center'}>
                          <Text weight="semibold">{header.name}</Text>
                          <Tooltip placement="top" content={header.tooltipText} zIndex={1200}>
                            <TooltipInteractiveWrapper
                              zIndex={99999999}
                              display={'flex'}
                              alignItems={'center'}>
                              <InfoIcon size="medium" />
                            </TooltipInteractiveWrapper>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Text weight="semibold">{header.name}</Text>
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <ListViewLoader
                  columnCount={4}
                  accessibilityLabel="loading flexible benefits components"
                />
              ) : (
                salaryComponents.map((salaryComponent) => {
                  return (
                    <TableRow
                      key={salaryComponent.id}
                      item={salaryComponent}
                      onClick={() => openDetailsView(salaryComponent.id)}
                      hoverActions={
                        <Button
                          variant="tertiary"
                          color="primary"
                          size="medium"
                          icon={ArrowRightIcon}
                          iconPosition="right"
                          onClick={() => openDetailsView(salaryComponent.id)}
                          testID={`view-details-button-${salaryComponent.id}`}>
                          View Details
                        </Button>
                      }>
                      <TableCell>
                        <TextWithDescription
                          componentName={salaryComponent.name}
                          description={
                            salaryComponent.component_space !== 'tenant' ? 'Predefined' : ''
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Text>{salaryComponent?.display_name ?? salaryComponent.name}</Text>
                      </TableCell>

                      <TableCell>
                        {(() => {
                          const { displayValue, description } = formatBenefitAmount(
                            salaryComponent.settings?.flexible_settings?.benefit_amount_settings,
                            isFbpPercentagePrecisionEnabled ? salaryComponent.response_precision : undefined,
                          );
                          return (
                            <TextWithDescription
                              componentName={displayValue}
                              description={description}
                            />
                          );
                        })()}
                      </TableCell>

                      <TableCell>
                        <Box>
                          <StatusBadge isActive={salaryComponent.is_active} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </>
        )}
      </Table>
      {isTableEmpty && !isLoading && !isRefreshing && !isError && (
        <EmptyListView
          title={
            isAnyFilterApplied
              ? 'No Search Results Found'
              : 'No Flexible Benefits components created yet 🙈'
          }
          description={
            <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
              Click on Create Component button to create your first component
            </Text>
          }>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  history.push(routePaths.flexibleBenefits.create);
                }}
                isFullWidth>
                Create Flexible Component
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
      {isError && (
        <EmptyListView
          title={`There was an error while fetching the data for ${
            currentFilters.status ?? 'all'
          } flexible benefits components. `}
          description={errorString ? `Error: ${errorString}` : null}>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore}
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={onRefresh}
                isFullWidth
                iconPosition="left"
                icon={RefreshIcon}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
    </>
  );
};
